import { useCallback, useEffect, useRef, useState } from 'react';
import { readStored, writeStored } from './storage';

/** Debounced copy of a value, for search boxes that query the server. */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Persisted state with the same shape as useState. */
export function useStoredState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readStored(key, initial));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      writeStored(key, next);
    },
    [key],
  );
  return [value, set];
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (): void => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** Runs `handler` on a global keyboard shortcut, e.g. mod+k. */
export function useHotkey(key: string, handler: (event: KeyboardEvent) => void, options: { mod?: boolean } = {}): void {
  const saved = useRef(handler);
  useEffect(() => {
    saved.current = handler;
  }, [handler]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const modPressed = event.metaKey || event.ctrlKey;
      if (event.key.toLowerCase() === key.toLowerCase() && (options.mod ? modPressed : !modPressed)) {
        saved.current(event);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, options.mod]);
}
